/* Photographic preview only. Product, price, inventory and cart data stay in configurator.js. */
(() => {
  const W = 344;
  const UNIT = W / 37;
  const PAD = 24;
  const COLOURS = {
    cloud: [1, 1, 1],
    dune: [0.5455297597, 0.3908208070, 0.3186184430],
    storm: [0.2452275517, 0.2936031423, 0.4303508166]
  };
  // Source photograph's curved boundaries, sampled from the approved Cloud master.
  const PARTS = {
    nest: [50, -22, 226, -14],
    small_spacer: [228, -14, 523, -8],
    large_spacer: [524, -8, 1013, 3],
    pod: [1014, 3, 1467, 19]
  };
  const textures = {};
  const cache = new Map();
  let baseImage;
  let ready = false;

  function image(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }
  function canvas(width, height) {
    const el = document.createElement('canvas');
    el.width = Math.ceil(width);
    el.height = Math.ceil(height);
    return el;
  }
  function toLinear(n) { n /= 255; return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4; }
  function toSrgb(n) { return Math.round(255 * (n <= 0.0031308 ? n * 12.92 : 1.055 * n ** (1 / 2.4) - 0.055)); }

  async function load(sources) {
    if (!sources?.cloud || !sources?.base) return false;
    try {
      const [master, base] = await Promise.all([image(sources.cloud), image(sources.base)]);
      baseImage = base;
      for (const [colour, multipliers] of Object.entries(COLOURS)) {
        const surface = canvas(master.naturalWidth, master.naturalHeight);
        const ctx = surface.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(master, 0, 0);
        if (colour !== 'cloud') {
          const pixels = ctx.getImageData(0, 0, surface.width, surface.height);
          const lut = multipliers.map(m => Array.from({ length: 256 }, (_, i) => toSrgb(toLinear(i) * m)));
          for (let i = 0; i < pixels.data.length; i += 4) {
            for (let c = 0; c < 3; c++) pixels.data[i + c] = lut[c][pixels.data[i + c]];
          }
          ctx.putImageData(pixels, 0, 0);
        }
        textures[colour] = surface;
      }
      ready = true;
      return true;
    } catch (error) {
      // A failed photo download must never prevent configuring or purchasing a build.
      console.warn('Catterpod photo preview unavailable; using original component images.');
      return false;
    }
  }

  function geometry(height, top, onBase) {
    const h = height * UNIT;
    const topCurve = top ? -20 : -8;
    const bottomCurve = onBase ? 14 : -8;
    // Both sides of every internal joint use this same curve and anchor.
    const path = `M 0 3 Q 0 0 3 -0.3 Q ${W / 2} ${topCurve * 2} ${W - 3} -0.3 Q ${W} 0 ${W} 3 L ${W} ${h - 3} Q ${W} ${h} ${W - 3} ${h + bottomCurve * 0.0346} Q ${W / 2} ${h + bottomCurve * 2} 3 ${h + bottomCurve * 0.0346} Q 0 ${h} 0 ${h - 3} Z`;
    return { h, topCurve, bottomCurve, path };
  }

  function part(type, colour, height, top, onBase) {
    const key = [type, colour, height, top, onBase].join(':');
    if (cache.has(key)) return cache.get(key);
    const g = geometry(height, top, onBase);
    const surface = canvas(W + 6, g.h + PAD * 2);
    const ctx = surface.getContext('2d');
    ctx.translate(3, PAD);
    ctx.clip(new Path2D(g.path));
    const [sTop, sTopCurve, sBottom, sBottomCurve] = PARTS[type];
    for (let x = 0; x < W; x++) {
      const curve = 1 - (2 * (x + 0.5) / W - 1) ** 2;
      const sourceTop = sTop + sTopCurve * curve;
      const sourceBottom = sBottom + sBottomCurve * curve;
      const destTop = g.topCurve * curve;
      const destBottom = g.h + g.bottomCurve * curve;
      ctx.drawImage(textures[colour], 341 + x, sourceTop, 1, sourceBottom - sourceTop, x, destTop - 0.6, 1, destBottom - destTop + 1.2);
    }
    // Retain a subtle contact seam; no white gap between photographic components.
    ctx.strokeStyle = 'rgba(54, 45, 32, 0.16)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(2, g.h);
    ctx.quadraticCurveTo(W / 2, g.h + g.bottomCurve * 2, W - 2, g.h);
    ctx.stroke();
    const result = { surface, path: g.path, x: -3, y: -PAD, width: W + 6, height: surface.height, units: UNIT };
    cache.set(key, result);
    return result;
  }

  function base() {
    if (cache.has('base')) return cache.get('base');
    const width = 40 * UNIT;
    const height = 4 * UNIT;
    const surface = canvas(width, height);
    const ctx = surface.getContext('2d');
    // Full top ellipse and front rim from the real black Base product photograph.
    const path = `M 0 ${height * .38} C 0 ${-height * .05} ${width} ${-height * .05} ${width} ${height * .38} L ${width} ${height * .72} C ${width} ${height * 1.09} 0 ${height * 1.09} 0 ${height * .72} Z`;
    ctx.clip(new Path2D(path));
    ctx.drawImage(baseImage, 294, 1217, 1230, 119, 0, 0, width, height);
    const result = { surface, path, x: 0, y: -UNIT, width, height, units: UNIT };
    cache.set('base', result);
    return result;
  }

  function create({ type, colour, height, scale, top, onBase, label }) {
    if (!ready || (type !== 'base' && (!PARTS[type] || !COLOURS[colour]))) return null;
    const piece = type === 'base' ? base() : part(type, colour, height, top, onBase);
    const ratio = scale / piece.units;
    const container = document.createElement('div');
    container.className = 'cp-photo';
    container.style.cssText = `position:absolute;left:${piece.x * ratio}px;top:${piece.y * ratio}px;width:${piece.width * ratio}px;height:${piece.height * ratio}px;pointer-events:none;`;
    const output = canvas(piece.surface.width, piece.surface.height);
    output.getContext('2d').drawImage(piece.surface, 0, 0);
    output.style.cssText = 'display:block;width:100%;height:100%;';
    output.setAttribute('aria-hidden', 'true');
    container.appendChild(output);
    const NS = 'http://www.w3.org/2000/svg';
    const outline = document.createElementNS(NS, 'svg');
    outline.setAttribute('viewBox', `${piece.x} ${type === 'base' ? 0 : piece.y} ${piece.width} ${piece.height}`);
    outline.setAttribute('aria-hidden', 'true');
    outline.setAttribute('class', 'cp-photo-outline');
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', piece.path);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#61724d');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    outline.appendChild(path);
    container.appendChild(outline);
    const hit = document.createElement('button');
    hit.type = 'button';
    hit.className = 'cp-photo-highlight';
    hit.setAttribute('aria-label', `Highlight ${label}`);
    container.appendChild(hit);
    return container;
  }
  window.CatterpodPhotoPreview = { load, create };
})();
